import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StageBadge } from '@/components/StageBadge';
import { ChevronRight, Shield, Unlock, AlertTriangle } from 'lucide-react';
import type { JobGovernanceCapacity } from '@/hooks/useJobGovernance';
import { Trans, useTranslation } from 'react-i18next';

interface GovernedAdvanceDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
  fromStage: string;
  toStage: string;
  candidateName: string;
  capacity: JobGovernanceCapacity | null | undefined;
}

export function GovernedAdvanceDialog({
  open,
  onConfirm,
  onCancel,
  isPending,
  fromStage,
  toStage,
  candidateName,
  capacity,
}: GovernedAdvanceDialogProps) {
  const { t } = useTranslation();
  const isGoverned = capacity?.governed && !capacity.is_unlimited;
  const remaining = capacity?.remaining ?? 0;
  const remainingAfter = Math.max(remaining - 1, 0);

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            {t('hire.governedAdvanceDialog.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                <Trans
                  i18nKey="hire.governedAdvanceDialog.body"
                  values={{ candidateName }}
                  components={{ strong: <strong /> }}
                />
              </p>

              <div className="flex items-center gap-2 justify-center py-2">
                <StageBadge stage={fromStage} />
                <ChevronRight className="w-4 h-4 text-muted-foreground rtl-flip" />
                <StageBadge stage={toStage} />
              </div>

              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2 text-sm">
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <Unlock className="w-4 h-4 text-primary" />
                  {t('hire.governedAdvanceDialog.actionWill')}
                </div>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ms-6">
                  <li>{t('hire.governedAdvanceDialog.unlockCv')}</li>
                  <li>
                    <Trans
                      i18nKey="hire.governedAdvanceDialog.consumePass"
                      components={{ strong: <strong /> }}
                    />
                  </li>
                  <li>{t('hire.governedAdvanceDialog.cannotReverse')}</li>
                </ul>
              </div>

              {isGoverned && (
                <div className={`rounded-lg border p-3 text-sm ${remaining <= 1 ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-muted/30'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('hire.governedAdvanceDialog.passesRemainingAfter')}</span>
                    <span className={`font-bold tabular-nums ${remainingAfter <= 1 ? 'text-destructive' : 'text-foreground'}`}>
                      {remainingAfter}
                    </span>
                  </div>
                  {remaining <= 1 && (
                    <div className="flex items-center gap-1.5 mt-2 text-destructive text-xs">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {remaining === 0
                        ? t('hire.governedAdvanceDialog.noPassesRemaining')
                        : t('hire.governedAdvanceDialog.lastPipelinePass')}
                    </div>
                  )}
                </div>
              )}

              {capacity?.is_unlimited && (
                <p className="text-xs text-muted-foreground italic text-center">
                  {t('hire.governedAdvanceDialog.unlimitedPasses')}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isPending || (isGoverned && remaining <= 0)}>
            {isPending ? t('hire.governedAdvanceDialog.processing') : t('hire.governedAdvanceDialog.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
